import { useEffect, useState } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { trpc } from "@/lib/trpc";
import { toast } from "@/hooks/use-toast";
import type { PurchaseOrderItem } from "@/features/purchase-orders/types";
import { Money } from "./Money";

interface PriceOverrideCellProps {
  item: PurchaseOrderItem;
  canOverride: boolean;
  currency: string | null;
  /** BLU-373 PR2b: PO exchange rate so the read-mode cell can flip to org base */
  exchangeRate: number | null;
  orderId: string;
  /**
   * BLU-373 PR3 — when provided, the parent owns the editing state (e.g. via
   * a dropdown menu in the cell). The internal pencil trigger is hidden in
   * read mode; the parent decides when to start editing.
   */
  externalEdit?: {
    isEditing: boolean;
    onEditingChange: (next: boolean) => void;
  };
}

export function PriceOverrideCell({
  item,
  canOverride,
  currency,
  exchangeRate,
  orderId,
  externalEdit,
}: PriceOverrideCellProps) {
  const [isEditingInternal, setIsEditingInternal] = useState(false);
  const [editValue, setEditValue] = useState("");

  const isEditing = externalEdit ? externalEdit.isEditing : isEditingInternal;
  const setIsEditing = externalEdit ? externalEdit.onEditingChange : setIsEditingInternal;
  // Hide the internal pencil trigger when the parent owns the dropdown trigger
  const showInternalTrigger = !externalEdit;

  // When the parent flips to edit mode, prime the input with the current price
  useEffect(() => {
    if (externalEdit?.isEditing && !editValue) {
      const currentPrice = item.overridden_unit_price ?? item.unit_cost;
      setEditValue(currentPrice != null ? String(currentPrice) : "");
    }
    if (!externalEdit?.isEditing) {
      setEditValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalEdit?.isEditing]);

  const utils = trpc.useUtils();
  const mutation = trpc.purchaseOrders.overrideItemPrice.useMutation({
    onSuccess: () => {
      utils.purchaseOrders.getOrders.invalidate();
      toast({ title: "Price override saved" });
      setIsEditing(false);
    },
    onError: (err) => {
      toast({
        title: "Failed to save price override",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const hasOverride = item.overridden_unit_price != null;

  const handleStartEdit = () => {
    const currentPrice = item.overridden_unit_price ?? item.unit_cost;
    setEditValue(currentPrice != null ? String(currentPrice) : "");
    setIsEditing(true);
  };

  const handleSave = () => {
    const parsed = parseFloat(editValue);
    if (isNaN(parsed) || parsed <= 0) {
      toast({
        title: "Invalid price",
        description: "Price must be a positive number",
        variant: "destructive",
      });
      return;
    }
    const rounded = Math.round(parsed * 100) / 100;
    mutation.mutate({
      itemId: item.id,
      orderId,
      overriddenUnitPrice: rounded,
    });
  };

  const handleClear = () => {
    mutation.mutate({
      itemId: item.id,
      orderId,
      overriddenUnitPrice: null,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue("");
  };

  const handleInputChange = (value: string) => {
    if (value === "") {
      setEditValue("");
      return;
    }
    if (!/^\d*\.?\d{0,2}$/.test(value)) return;
    setEditValue(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  // Edit mode
  if (isEditing) {
    const symbol = currency === "USD" ? "$" : currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
    return (
      <div className="flex flex-col items-end gap-0.5">
        <div className="flex items-center gap-1">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              {symbol}
            </span>
            <Input
              type="text"
              inputMode="decimal"
              className="h-8 w-[100px] text-right tabular-nums pl-8 text-sm"
              value={editValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              aria-label="Override price"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 p-0"
            onClick={handleSave}
            disabled={mutation.isPending}
            aria-label="Confirm price override"
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4 text-green-600" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 p-0"
            onClick={handleCancel}
            disabled={mutation.isPending}
            aria-label="Cancel price override edit"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
        <span className="text-xs text-muted-foreground">
          Sets the cost for this line item
        </span>
      </div>
    );
  }

  // Read mode with override — badge + actions LEFT, costs RIGHT
  if (hasOverride) {
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Badge variant="info" size="sm">Override</Badge>
          {canOverride && showInternalTrigger && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={handleStartEdit}
                    disabled={mutation.isPending}
                    aria-label="Edit price override for this line"
                  >
                    <Pencil className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Edit override</TooltipContent>
              </Tooltip>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleClear}
                disabled={mutation.isPending}
                aria-label="Clear price override"
              >
                {mutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <X className="h-3 w-3 text-muted-foreground hover:text-red-500" />
                )}
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {item.unit_cost != null && (
            <Money
              amount={item.unit_cost}
              poCurrency={currency}
              exchangeRate={exchangeRate}
              className="text-xs text-muted-foreground line-through"
            />
          )}
          <Money
            amount={item.overridden_unit_price}
            poCurrency={currency}
            exchangeRate={exchangeRate}
            className="text-sm font-medium"
          />
        </div>
      </div>
    );
  }

  // Read mode without override
  return (
    <div className="flex items-center justify-end gap-1">
      <Money
        amount={item.unit_cost}
        poCurrency={currency}
        exchangeRate={exchangeRate}
        className="text-sm"
      />
      {canOverride && showInternalTrigger && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleStartEdit}
              aria-label="Edit price override for this line"
            >
              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            Override the unit price for this line
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
